import { useCallback, useEffect, useState } from 'react';
import { AppBar } from '../components/AppBar';
import { QueueStatus } from '../components/QueueStatus';
import { Icon } from '../components/icons';
import { listRoutes } from '../api/endpoints';
import { ApiError } from '../api/types';
import type { RouteSummary } from '../domain/routes';
import { useT } from '../i18n/I18nProvider';
import { todayIso } from '../domain/dates';

interface Props {
  onOpenRoute: (routeNo: string, date: string) => void;
  onBack: () => void;
  onLogout: () => void;
}

/**
 * Přehled tras pro mistra (role `MASTER`).
 *
 * Řazení: nedokončené trasy nahoru. Mistr sem chodí hledat, co ještě není
 * naložené — hotové trasy jsou jen potvrzení, že se nic neztratilo.
 */
export function RoutesScreen({ onOpenRoute, onBack, onLogout }: Props) {
  const { t } = useT();
  const [date, setDate] = useState(todayIso);
  const [routes, setRoutes] = useState<RouteSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [onlyUnfinished, setOnlyUnfinished] = useState(true);

  const load = useCallback(
    async (day: string) => {
      setRoutes(null);
      setError(null);
      try {
        setRoutes(await listRoutes(day));
      } catch (err) {
        console.error('[routes] načtení tras selhalo:', err);
        setError(err instanceof ApiError && err.kind === 'FORBIDDEN' ? t('routesForbidden') : t('routesErr'));
      }
    },
    [t],
  );

  useEffect(() => {
    void load(date);
  }, [date, load]);

  const shown = (routes ?? [])
    .filter((r) => !onlyUnfinished || !r.complete)
    .sort((a, b) => Number(a.complete) - Number(b.complete) || a.routeNo.localeCompare(b.routeNo));

  return (
    <div className="app">
      <AppBar onLogout={onLogout} />

      <div className="screen-pad">
        <QueueStatus />

        <div className="rt-head">
          <h2 className="h2">{t('routesTitle')}</h2>
          <label className="rt-date">
            <span className="k">{t('routesDay')}</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value || todayIso())} />
          </label>
        </div>

        <div className="langs rt-filter" role="group" aria-label={t('routesUnfinishedCount')}>
          <button type="button" aria-pressed={onlyUnfinished} onClick={() => setOnlyUnfinished(true)}>
            {t('routesUnfinishedOnly')}
          </button>
          <button type="button" aria-pressed={!onlyUnfinished} onClick={() => setOnlyUnfinished(false)}>
            {t('routesAll')}
          </button>
        </div>

        {error && <p className="status err">{error}</p>}
        {!error && routes === null && <p className="status">{t('routesLoading')}</p>}
        {!error && routes !== null && shown.length === 0 && (
          <p className="status">{onlyUnfinished && routes.length > 0 ? t('routeNoUnfinished') : t('routesEmpty')}</p>
        )}

        {shown.map((route) => (
          <button
            key={route.routeNo}
            type="button"
            className={`rt-card${route.complete ? ' done' : ''}${route.mine ? ' mine' : ''}`}
            onClick={() => onOpenRoute(route.routeNo, date)}
          >
            <span className="rt-row">
              <span className="rt-no">{route.routeNo}</span>
              <span className="rt-meta">
                <b>{route.driverName ?? t('routeNoDriver')}</b>
                {route.vehicle && <small>{route.vehicle}</small>}
                {route.plate && <small className="mono">{route.plate}</small>}
              </span>
              {route.mine && <span className="chip">{t('routesMine')}</span>}
            </span>

            <span className="rt-nums">
              <span>
                <span className="k">{t('routeShipments')}</span>
                <b>{route.shipments}</b>
              </span>
              <span>
                <span className="k">{t('routeLoaded')}</span>
                <b>
                  {route.loadedParcels} / {route.parcels}
                </b>
              </span>
              {/* Nenaskenované balíky mají přednost před nenaloženými: nenaložený
                  balík ve skladu leží, nenaskenovaný v něm nemusí být vůbec. */}
              {route.complete ? (
                <span className="ok">
                  <Icon name="check" />
                  {t('routeAllDone')}
                </span>
              ) : route.unseenParcels > 0 ? (
                <span className="miss">
                  <Icon name="warn" />
                  {t('routeUnseen')}: {route.unseenParcels}
                </span>
              ) : (
                <span className="todo">
                  <Icon name="warn" />
                  {t('routeToLoad')}: {route.remainingParcels}
                </span>
              )}
            </span>
          </button>
        ))}

        <button className="btn" type="button" onClick={onBack}>
          {t('scanNext')}
        </button>
      </div>
    </div>
  );
}
